import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import inventoryService from '../../services/inventoryService';
import supplierService, { Supplier } from '../../services/supplierService';
import { useToast } from '../../hooks/useToast';
import * as ExcelJS from 'exceljs';
import LoadingSpinner from '../ui/LoadingSpinner';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 32px;
  border-radius: 12px;
  width: 600px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const SubmittingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
`;

const SubmittingText = styled.p`
  margin-top: 1rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
`;

// Confirmation Modal for duplicate SKUs
const ConfirmationModal = styled(Modal)`
  z-index: 1100;
`;

const ConfirmationContent = styled(ModalContent)`
  width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ConfirmationTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #042B41;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ConfirmationText = styled.p`
  font-size: 16px;
  color: #333;
  margin-bottom: 16px;
  text-align: center;
`;

const DuplicatesList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background-color: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 24px;
`;

const DuplicateItem = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #042B41;
  padding: 4px 0;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #042B41;
  margin: 0 0 24px 0;
  text-align: center;
`;

const UploadArea = styled.div`
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  margin-bottom: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #042B41;
    background-color: #f9fafb;
  }
`;

const UploadText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
`;

const FileName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #042B41;
  background-color: #f0f5ff;
  padding: 10px 16px;
  border-radius: 6px;
  margin-top: 16px;
  display: inline-block;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
`;

const Button = styled.button`
  padding: 12px 0;
  width: 180px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.secondary {
    background-color: white;
    color: #333;
    border: 1px solid #d0d0d0;
    
    &:hover {
      background-color: #f5f5f5;
    }
  }
  
  &.primary {
    background-color: #042B41;
    color: white;
    border: 1px solid #042B41;
    
    &:hover {
      background-color: #03223A;
    }
    
    &:disabled {
      background-color: #89a2b3;
      border-color: #89a2b3;
      cursor: not-allowed;
    }
  }
  
  &.warning {
    background-color: #dc2626;
    color: white;
    border: 1px solid #dc2626;
    
    &:hover {
      background-color: #b91c1c;
    }
    
    &:disabled {
      background-color: #fca5a5;
      border-color: #fca5a5;
      cursor: not-allowed;
    }
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ResultContainer = styled.div`
  margin-top: 24px;
  border-top: 1px solid #eee;
  padding-top: 24px;
`;

const ResultTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #042B41;
  margin: 0 0 16px 0;
`;

const SuccessMessage = styled.p`
  color: #22c55e;
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 8px;
`;

const ErrorContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background-color: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
`;

const ErrorItem = styled.p`
  color: #ef4444;
  font-size: 14px;
  margin: 4px 0;
`;

const DownloadTemplateLink = styled.a`
  color: #042B41;
  text-decoration: underline;
  font-size: 14px;
  display: block;
  margin-top: 16px;
  text-align: center;
  cursor: pointer;
  
  &:hover {
    color: #03223A;
  }
`;

const InfoText = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 24px;
  padding: 12px;
  background-color: #f8fafc;
  border-radius: 6px;
  border-left: 4px solid #042B41;
`;

// Validation Error Alert Modal Styled Components
const AlertModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1rem;
`;

const AlertModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 480px) {
    padding: 1rem;
    max-width: 100%;
  }
`;

const AlertModalTitle = styled.h2`
  font-size: 1.125rem;
  color: #DC2626;
  margin: 0 0 0.75rem 0;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const AlertModalMessage = styled.div`
  color: #111827;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  word-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0.5rem;
  white-space: pre-line;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const AlertButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.75rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const AlertCancelButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  background-color: white;
  color: #111827;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  min-height: 36px;
  
  &:hover {
    background-color: #F9FAFB;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 0.625rem;
    min-height: 36px;
  }
`;

interface BulkUploadModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

// Helper function to format error messages for display
const formatErrorMessage = (error: Error | string | unknown): string => {
  let errorMessage: string;
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else {
    errorMessage = 'Unknown error occurred';
  }
  
  const lowerErrorMessage = errorMessage.toLowerCase();
  
  // Check if error is about missing columns (even if prefixed with "Error processing file:")
  if (lowerErrorMessage.includes('missing required columns') || 
      lowerErrorMessage.includes('missing columns') ||
      (lowerErrorMessage.includes('required columns') && 
       (lowerErrorMessage.includes('sku code') || lowerErrorMessage.includes('style code')))) {
    return 'Invalid file: required columns are missing.\n\nPlease follow the template.';
  }
  
  // Check if error is about unsupported file format
  if (lowerErrorMessage.includes('unsupported file format') ||
      lowerErrorMessage.includes('unsupported file')) {
    return 'Invalid file format.\n\nPlease upload a CSV or Excel file (.csv, .xlsx, or .xls).';
  }
  
  // If error starts with "Error processing file:" but we haven't matched a specific pattern,
  // try to extract a more meaningful message or return a generic one
  if (lowerErrorMessage.startsWith('error processing file:')) {
    // Check if it's a column-related error that we might have missed
    if (lowerErrorMessage.includes('column') || lowerErrorMessage.includes('sku') || lowerErrorMessage.includes('style')) {
      return 'Invalid file: required columns are missing.\n\nPlease follow the template.';
    }
    // Otherwise, return a generic message without the technical prefix
    const cleanedMessage = errorMessage.replace(/^Error processing file:\s*/i, '').trim();
    return cleanedMessage || 'Invalid File: Required columns are missing. \n Please ensure that the file includes all mandatory fields \n listed in the upload guidelines.';
  }
  
  

  // Return the original error message if no specific pattern matches
  return errorMessage;
};

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [result, setResult] = useState<{ success: number; errors: number; error_details: string[] } | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [duplicateSkus, setDuplicateSkus] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showValidationErrorModal, setShowValidationErrorModal] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Fetch suppliers on component mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        // Use lightweight API to get only supplier names for dropdown
        const supplierNames = await supplierService.getSupplierNames();
        // Convert to Supplier format for compatibility
        const suppliersData = supplierNames.map(s => ({
          id: s.id,
          name: s.name,
          supplier_name: s.supplier_name,
          phone: '',
          phone_no: '',
          email: '',
          address: '',
          price_list: [],
          is_active: true
        }));
        setSuppliers(suppliersData);
      } catch (error) {
        console.error('Error fetching supplier names:', error);
        // Don't show toast in useEffect to prevent dependency issues
        // User will see that suppliers aren't loaded when they try to download template
      }
    };

    fetchSuppliers();
  }, []); // Empty dependency array - only run on mount

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      
      if (fileExtension === 'csv' || fileExtension === 'xlsx' || fileExtension === 'xls') {
        setFile(selectedFile);
        setResult(null); // Clear previous results
        setDuplicateSkus([]); // Clear previous duplicates
      } else {
        toast.showToast('Please select a valid CSV or Excel file', { type: 'error' });
      }
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const checkForDuplicates = async (uploadFile: File): Promise<string[]> => {
    try {
      setCheckingDuplicates(true);
      console.log('Checking for duplicate SKUs in file:', uploadFile.name);
      
      const duplicateResult = await inventoryService.checkDuplicateSkus(uploadFile);
      console.log('Duplicate check result:', duplicateResult);
      
      return duplicateResult.duplicates || [];
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      // Show validation error modal instead of just toast
      const formattedMessage = formatErrorMessage(error);
      setValidationErrorMessage(formattedMessage);
      setShowValidationErrorModal(true);
      setCheckingDuplicates(false);
      throw error; // Re-throw to stop the upload process
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      // First, check for duplicate SKUs
      const duplicates = await checkForDuplicates(file);
      
      if (duplicates.length > 0) {
        // Show confirmation dialog if duplicates found
        setDuplicateSkus(duplicates);
        setShowConfirmation(true);
        return;
      }
      
      // No duplicates found, proceed with normal upload
      await performUpload(false);
    } catch (error) {
      console.error('Error during upload process:', error);
      // Error modal is already shown by checkForDuplicates or performUpload
      // No need to show toast here as the modal provides better visibility
    }
  };

  const performUpload = async (overwrite: boolean = false) => {
    if (!file) return;

    try {
      setLoading(true);
      console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type, 'Overwrite:', overwrite);
      
      // Use the inventory service method which handles the overwrite parameter
      const result = await inventoryService.uploadInventoryFile(file, overwrite);
      setResult(result);
      
      if (result.success > 0 && result.errors === 0) {
        toast.showToast(`Successfully imported ${result.success} inventory items`, { type: 'success' });
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else if (result.success > 0) {
        toast.showToast(`Imported ${result.success} items with ${result.errors} errors`, { type: 'info' });
      } else {
        toast.showToast('Import failed. Please check the errors and try again.', { type: 'error' });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      // Show validation error modal instead of just toast
      const formattedMessage = formatErrorMessage(error);
      setValidationErrorMessage(formattedMessage);
      setShowValidationErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedWithOverwrite = async () => {
    setShowConfirmation(false);
    await performUpload(true); // Upload with overwrite = true
  };

  const handleCancelOverwrite = () => {
    setShowConfirmation(false);
    // Close the main modal as well
    onClose();
  };

  const handleCloseValidationErrorModal = () => {
    setShowValidationErrorModal(false);
    setValidationErrorMessage('');
  };

  const downloadTemplate = async () => {
    // Create template with supplier dropdown using ExcelJS
    if (suppliers.length === 0) {
      toast.showToast('Please wait for suppliers to load before downloading template', { type: 'warning' });
      return;
    }

    try {
      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Inventory Template');
      
      // Define headers
      const headers = [
        'SKU Code', 'Description', 'Style Code', 'Supplier Name', 'Carton', 
        'Units per carton', 'Carton Dimensions', 'Weight per unit', 
        'Weight per carton', 'No. of units per pallet', 'Pallet weight', 'Available Stock'
      ];
      
      // Add headers to the first row
      worksheet.addRow(headers);
      
      // Style the header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
      
      // Set column widths
      worksheet.columns = [
        { width: 15 }, // SKU Code
        { width: 25 }, // Description
        { width: 15 }, // Style Code
        { width: 20 }, // Supplier Name
        { width: 10 }, // Carton
        { width: 15 }, // Units per carton
        { width: 20 }, // Carton Dimensions
        { width: 15 }, // Weight per unit
        { width: 15 }, // Weight per carton
        { width: 20 }, // No. of units per pallet
        { width: 15 }, // Pallet weight
        { width: 15 }  // Available Stock
      ];
      
      // Create supplier names list for dropdown
      const supplierNames = suppliers.map(s => s.name || s.supplier_name).filter(Boolean);
      
      // Add data validation for Supplier Name column (column D, index 4)
      // Apply validation to rows 2-1000 to allow for plenty of data
      for (let row = 2; row <= 1000; row++) {
        worksheet.getCell(`D${row}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`"${supplierNames.join(',')}"`],
          showErrorMessage: true,
          errorStyle: 'stop',
          errorTitle: 'Invalid Supplier',
          error: 'Please select a supplier from the dropdown list.'
        };
      }
      

      
      // Generate Excel file buffer
      const buffer = await workbook.xlsx.writeBuffer();
      
      // Create blob and download
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory_template.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Show success toast
      toast.showToast('Excel template downloaded! Supplier names are available in dropdown.', { type: 'success' });
    } catch (error) {
      console.error('Error creating Excel template:', error);
      toast.showToast('Failed to create Excel template. Please try again.', { type: 'error' });
    }
  };

  return (
    <>
      <Modal>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          {(loading || checkingDuplicates) && (
            <SubmittingOverlay>
              <LoadingSpinner size="lg" />
              <SubmittingText>
                {checkingDuplicates ? 'Checking for duplicate SKUs...' : 'Uploading inventory file...'}
              </SubmittingText>
            </SubmittingOverlay>
          )}
          <Title>Bulk Upload Inventory</Title>
          
          <UploadArea onClick={handleUploadClick}>
            <UploadText>Click or drag file to this area to upload</UploadText>
            <UploadText>Support for CSV, XLS, XLSX files</UploadText>
            <FileInput 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".csv,.xlsx,.xls"
            />
            {file && <FileName>{file.name}</FileName>}
          </UploadArea>
          
          <ButtonGroup>
            <Button className="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              className="primary" 
              onClick={handleUpload}
              disabled={!file || loading || checkingDuplicates}
            >
              {checkingDuplicates ? 'Checking...' : loading ? 'Uploading' : 'Upload'}
            </Button>
          </ButtonGroup>
          
          <DownloadTemplateLink onClick={downloadTemplate}>
            Download Excel Template (with Supplier Dropdown)
          </DownloadTemplateLink>
          
          {result && (
            <ResultContainer>
              <ResultTitle>Upload Results</ResultTitle>
              {result.success > 0 && (
                <SuccessMessage>Successfully imported {result.success} inventory items</SuccessMessage>
              )}
              
              {result.errors > 0 && (
                <>
                  <ErrorItem>Failed to import {result.errors} items</ErrorItem>
                  <ErrorContainer>
                    {result.error_details.map((error, index) => (
                      <ErrorItem key={index}>{error}</ErrorItem>
                    ))}
                  </ErrorContainer>
                </>
              )}
            </ResultContainer>
          )}
          
          <InfoText>
            <p><strong>Required Fields:</strong> SKU Code, Style Code</p>
            <p><strong>Optional Fields:</strong> Description, Supplier Name, Carton, Units per carton, Carton Dimensions, Weight per unit, Weight per carton, No. of units per pallet, Pallet weight, Available Stock</p>
            <p>Carton Dimensions should be in format: LxWxH cm (e.g. 40x30x25 cm)</p>
            <p><strong>Supplier Name:</strong> Download the Excel template for a dropdown list of available suppliers. The system will automatically map supplier names to IDs.</p>
            <p><strong>File Format:</strong> Use the Excel template (.xlsx) for best experience with dropdown validation, or upload CSV/Excel files with exact supplier names.</p>
          </InfoText>
        </ModalContent>
      </Modal>

      {/* Confirmation Modal for Duplicate SKUs */}
      {showConfirmation && (
        <ConfirmationModal onClick={(e) => e.stopPropagation()}>
          <ConfirmationContent onClick={(e) => e.stopPropagation()}>
            <ConfirmationTitle>Duplicate SKUs Found</ConfirmationTitle>
            <ConfirmationText>
              Do you want to update the following SKUs?
            </ConfirmationText>
            
            <DuplicatesList>
              {duplicateSkus.map((sku, index) => (
                <DuplicateItem key={index}>{sku}</DuplicateItem>
              ))}
            </DuplicatesList>
            
            <ConfirmationText>
              Existing data for these SKUs will be overwritten with the new data from your uploaded file.
            </ConfirmationText>
            
            <ButtonGroup>
              <Button className="secondary" onClick={handleCancelOverwrite}>
                Cancel
              </Button>
              <Button 
                className="warning" 
                onClick={handleProceedWithOverwrite}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Proceed'}
              </Button>
            </ButtonGroup>
          </ConfirmationContent>
        </ConfirmationModal>
      )}

      {/* Validation Error Alert Modal */}
      {showValidationErrorModal && (
        <AlertModalOverlay onClick={handleCloseValidationErrorModal}>
          <AlertModalContent onClick={(e) => e.stopPropagation()}>
            <AlertModalTitle>File Validation Error</AlertModalTitle>
            <AlertModalMessage>{validationErrorMessage}</AlertModalMessage>
            <AlertButtonContainer>
              <AlertCancelButton onClick={handleCloseValidationErrorModal}>
                Close
              </AlertCancelButton>
            </AlertButtonContainer>
          </AlertModalContent>
        </AlertModalOverlay>
      )}
    </>
  );
};

export default BulkUploadModal; 